// @flow

import React from 'react';
import styled from 'styled-components';
import { DangerTertiaryButtonTheme, TertiaryButtonTheme } from './theme';
import { CommonButtonStyles } from './shared';
import type { TertiaryButtonColorTheme } from './theme';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';

type TertiaryButtonProps = {|
  /**
   * If true, the button is not clickable and the `onClick` will not fire.
   * Additionally, the button will appear at half opacity.
   */
  disabled?: boolean,
  /**
   * Fired when button is clicked.
   */
  onClick: (evt: SyntheticEvent<HTMLButtonElement>) => void,
  /**
   * Contents of the button, must be text.
   */
  children: string,
  /**
   * A classname that is added to the button element if provided.
   */
  className?: string,
  ...DataProvidesProps,
|};

const defaultButtonProps = {
  disabled: false,
};

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const TertiaryButtonWrapper = styled.button`
  ${CommonButtonStyles}

  padding: 10px 0;
  text-decoration: underline;
  transition: none;

  background: none;
  border: none;

  &:active {
    box-shadow: none;
  }

  &:hover,
  &:focus {
    background: none;
    text-decoration: underline;
    border: none;
  }

  ${({ theme }) => `
    color: ${theme.default.color};

    &:hover,
    &:focus {
      color: ${theme.hover.color};
    }
  `}
`;

const ThemeableTertiaryButton = (props: {
  ...TertiaryButtonProps,
  theme: TertiaryButtonColorTheme,
}) => {
  const { children, theme, disabled, onClick, className } = props;

  return (
    <TertiaryButtonWrapper
      theme={theme}
      disabled={disabled}
      aria-disabled={props.disabled}
      onClick={onClick}
      className={className}
      {...getDataProvidesProps(props)}
    >
      {children}
    </TertiaryButtonWrapper>
  );
};
ThemeableTertiaryButton.defaultProps = defaultButtonProps;

const TertiaryButton = (props: TertiaryButtonProps) => (
  <ThemeableTertiaryButton {...props} theme={TertiaryButtonTheme} />
);
TertiaryButton.displayName = 'TertiaryButton';
TertiaryButton.defaultProps = {
  ...defaultButtonProps,
  'data-provides': 'tertiary-button',
};

const DangerTertiaryButton = (props: TertiaryButtonProps) => (
  <ThemeableTertiaryButton {...props} theme={DangerTertiaryButtonTheme} />
);
DangerTertiaryButton.displayName = 'DangerTertiaryButton';
DangerTertiaryButton.defaultProps = {
  ...defaultButtonProps,
  'data-provides': 'danger-tertiary-button',
};

export { TertiaryButton, DangerTertiaryButton };
export type { TertiaryButtonProps };
